import { DEFAULT_SCORING } from './gameScoreRules';
import { getBestCourtAssignmentsTwoStage } from './matchmakingUtils';
import { updatePlayerInteractions } from './matchmakingUtils';
import { detectGameType } from './gameTypeUtils';
import { categorizePlayersForSession } from './playerCategorizationUtils';
import { calculateExpectedScore, ELO_POLICY_V2 } from './eloUtils';
import { MATCHMAKING_CONFIG } from '@badminton/matchmaking-core';
import { calculateTeamBasedElo } from './courtUtils';
import { computeSinglesEloResults } from './singlesEloCalculation';
import {
  getPlayersPerMatch,
  getPlayersPerSide,
  isSinglesFormat,
} from './competitiveMatchFormat';
import { getSinglesEloForComputation } from './playerUtils';

/**
 * Simulates a game result based on ELO probabilities
 * @param {Array} team1Players - Array of player objects for team 1
 * @param {Array} team2Players - Array of player objects for team 2
 * @param {Object} playersById - Map of player objects by ID
 * @param {boolean} isMixed - Whether this is a mixed doubles game
 * @returns {Object} Game result with winner and score
 */
function simulateGameResult(team1Players, team2Players, playersById, isMixed = false, isSinglesMatch = false) {
  const pickElo = (p) => {
    if (isMixed) return p.mixed_elo_rating || 1200;
    if (isSinglesMatch) return getSinglesEloForComputation(p);
    return p.levels_elo_rating || 1200;
  };
  const team1AvgElo = team1Players.reduce((sum, p) => sum + pickElo(p), 0) / team1Players.length;

  const team2AvgElo = team2Players.reduce((sum, p) => sum + pickElo(p), 0) / team2Players.length;

  // Calculate win probability for team 1
  const team1WinProbability = calculateExpectedScore(team1AvgElo, team2AvgElo);
  
  // Determine winner based on probability
  const random = Math.random();
  const team1Won = random < team1WinProbability;

  // Generate realistic scores (e.g. 21–15 or 15–10 depending on session points target)
  const winningScore = DEFAULT_SCORING.pointsTarget;
  const minLosing = Math.max(9, winningScore - 6);
  const losingScore = Math.max(minLosing, Math.floor(winningScore - (Math.random() * 6)));

  return {
    winner: team1Won ? 1 : 2,
    team1Score: team1Won ? winningScore : losingScore,
    team2Score: team1Won ? losingScore : winningScore,
  };
}

/**
 * Calculates ELO changes for all players in a game
 * @param {Array} team1Players - Array of player objects for team 1
 * @param {Array} team2Players - Array of player objects for team 2
 * @param {Object} result - Game result with winner and scores
 * @param {boolean} isMixed - Whether this is a mixed doubles game
 * @returns {Array} Updated player objects with new ELO ratings
 */
function calculateEloChanges(team1Players, team2Players, result, isMixed = false) {
  return calculateTeamBasedElo(
    team1Players,
    team2Players,
    result.winner === 1,
    0,
    isMixed,
    {
      eloPolicy: ELO_POLICY_V2,
      team1Score: result.team1Score,
      team2Score: result.team2Score,
    }
  );
}

/** Balanced singles (types 4/5): apply singles Elo; type 7 handled in caller (no rating change). */
function calculateSinglesRatedEloChanges(team1Players, team2Players, result, gameTypeId) {
  const computed = computeSinglesEloResults(team1Players[0], team2Players[0], {
    team1Won: result.winner === 1,
    team1Score: result.team1Score,
    team2Score: result.team2Score,
    gameTypeId,
    eloPolicy: ELO_POLICY_V2,
  });
  const mapPlayer = (player) => {
    const r = computed[player.id];
    if (!r) return { ...player, delta: 0 };
    return {
      ...player,
      singles_elo_rating: r.newRating,
      singles_games_played: (Number(player.singles_games_played) || 0) + 1,
      delta: r.delta,
    };
  };
  return [...team1Players.map(mapPlayer), ...team2Players.map(mapPlayer)];
}

/**
 * Updates session state after a game is completed
 * @param {Object} game - Completed game object
 * @param {Object} sessionState - Current session state
 */
function updateSessionState(game, sessionState) {
  // Update games played for each player
  const allPlayerIds = [...game.team1PlayerIds, ...game.team2PlayerIds];
  allPlayerIds.forEach(playerId => {
    if (!sessionState.gamesPlayedThisSession) {
      sessionState.gamesPlayedThisSession = {};
    }
    sessionState.gamesPlayedThisSession[playerId] = 
      (sessionState.gamesPlayedThisSession[playerId] || 0) + 1;
  });

  // Update player interactions
  updatePlayerInteractions(
    game.team1Players,
    game.team2Players,
    sessionState
  );

  // Add to match history
  if (!sessionState.matchHistory) {
    sessionState.matchHistory = [];
  }
  sessionState.matchHistory.push({
    team1: game.team1PlayerIds,
    team2: game.team2PlayerIds,
    winner: game.result.winner,
    gameType: game.gameType,
    createdAt: game.createdAt,
  });

  // Increment total games completed
  sessionState.totalGamesCompleted = (sessionState.totalGamesCompleted || 0) + 1;
}

/**
 * Updates player wait metrics (courts observed)
 * @param {Array} waitingList - Current waiting list
 * @param {Array} courts - Current court assignments
 * @param {Object} playerWaitMetrics - Current wait metrics
 */
function updateWaitMetrics(waitingList, playerWaitMetrics) {
  // Players in waiting list observe 1 game finishing (matching real session behavior)
  waitingList.forEach(playerId => {
    if (!playerWaitMetrics[playerId]) {
      playerWaitMetrics[playerId] = { courtsObserved: 0 };
    }
    // Increment by 1 each time a game finishes (matches real session)
    playerWaitMetrics[playerId].courtsObserved += 1;
  });
}

/**
 * Runs a full session simulation matching real-world flow
 * @param {Object} config - Simulation configuration
 * @param {Array} config.selectedPlayers - Array of selected player IDs
 * @param {number} config.numberOfCourts - Number of courts available
 * @param {Array} config.partnerships - Array of partnership objects
 * @param {Object} config.targetGames - Map of playerId to target games
 * @param {string} config.gameTypeRestriction - Game type restriction ('all_types', 'balanced_only', etc.)
 * @param {boolean} config.playerCategorization - Whether to use player categorization
 * @param {Object} config.playersById - Map of player objects by ID
 * @param {string} config.scoringSystem - 'legacy' or 'normalized'
 * @param {Object} config.matchmakingParams - Optional matchmaking parameters override (for legacy)
 * @param {Object} config.scoringConfig - Optional scoring config (for normalized)
 * @returns {Object} Simulation results
 */
export async function runSessionSimulation(config) {
  const { matchmakingParams = {}, scoringSystem = 'legacy', scoringConfig = null, varietyAwarePoolSelection = false, scoringFirstMatchmaking = false } = config;
  
  // Create a temporary config override for this simulation
  const originalConfig = { ...MATCHMAKING_CONFIG };
  const originalFeatureFlags = { ...MATCHMAKING_CONFIG.featureFlags };
  
  // Set feature flags based on scoring system
  if (scoringSystem === 'normalized') {
    MATCHMAKING_CONFIG.featureFlags.useNormalizedScoring = true;
    MATCHMAKING_CONFIG.featureFlags.compareScoringSystems = false;
    MATCHMAKING_CONFIG.featureFlags.scoringConfig = scoringConfig;
  } else {
    // Legacy scoring
    MATCHMAKING_CONFIG.featureFlags.useNormalizedScoring = false;
    MATCHMAKING_CONFIG.featureFlags.compareScoringSystems = false;
    MATCHMAKING_CONFIG.featureFlags.scoringConfig = null;
  }

  // Variety-aware pool selection flag (applies to both scoring systems)
  MATCHMAKING_CONFIG.featureFlags.varietyAwarePoolSelection = varietyAwarePoolSelection;
  MATCHMAKING_CONFIG.featureFlags.scoringFirstMatchmaking = scoringFirstMatchmaking;
  
  // Map simplified thresholds to the algorithm's new two-threshold system (legacy only)
  if (scoringSystem === 'legacy') {
    const sameTeamLow = matchmakingParams.sameTeamLowThreshold ?? MATCHMAKING_CONFIG.penalties.interactions.sameTeam.lowThreshold;
    const sameTeamHigh = matchmakingParams.sameTeamHighThreshold ?? MATCHMAKING_CONFIG.penalties.interactions.sameTeam.highThreshold;
    const totalLow = matchmakingParams.totalLowThreshold ?? MATCHMAKING_CONFIG.penalties.interactions.total.lowThreshold;
    const totalHigh = matchmakingParams.totalHighThreshold ?? MATCHMAKING_CONFIG.penalties.interactions.total.highThreshold;
  
    const configOverride = {
      waitGuarantee: {
        courtsObservedThreshold: matchmakingParams.waitGuaranteeThreshold ?? MATCHMAKING_CONFIG.waitGuarantee.courtsObservedThreshold,
      },
      penalties: {
        ...MATCHMAKING_CONFIG.penalties,
        categoryGapHardBlock: matchmakingParams.categoryGapHardBlock ?? MATCHMAKING_CONFIG.penalties.categoryGapHardBlock,
        interactions: {
          ...MATCHMAKING_CONFIG.penalties.interactions,
          sameTeam: {
            ...MATCHMAKING_CONFIG.penalties.interactions.sameTeam,
            lowThreshold: sameTeamLow,
            highThreshold: sameTeamHigh,
          },
          total: {
            ...MATCHMAKING_CONFIG.penalties.interactions.total,
            lowThreshold: totalLow,
            highThreshold: totalHigh,
          },
          consecutive: MATCHMAKING_CONFIG.penalties.interactions.consecutive,
        },
      },
      elo: {
        ...MATCHMAKING_CONFIG.elo,
        teamBalanceMultiplier: matchmakingParams.eloBalanceMultiplier ?? MATCHMAKING_CONFIG.elo.teamBalanceMultiplier,
      },
      consecutiveInteractionBlock: {
        enabled: matchmakingParams.enableConsecutiveInteractionBlock ?? MATCHMAKING_CONFIG.consecutiveInteractionBlock.enabled,
        threshold: matchmakingParams.consecutiveInteractionThreshold ?? MATCHMAKING_CONFIG.consecutiveInteractionBlock.threshold,
      },
    };
    
    // Store original and apply override
    Object.assign(MATCHMAKING_CONFIG, configOverride);
    
    // Store interaction penalty weight for use in readiness scoring
    const interactionPenaltyWeight = matchmakingParams.interactionPenaltyWeight ?? 0.4;
    const enableCooldown = matchmakingParams.enableCooldown !== undefined ? matchmakingParams.enableCooldown : true;
    const enableInteractionPenalties = matchmakingParams.enableInteractionPenalties !== undefined ? matchmakingParams.enableInteractionPenalties : true;
    
    // If interaction penalties are disabled, set all penalty values to 0
    if (!enableInteractionPenalties) {
      MATCHMAKING_CONFIG.penalties.interactions.sameTeam.lowPenalty = 0;
      MATCHMAKING_CONFIG.penalties.interactions.sameTeam.highPenalty = 0;
      MATCHMAKING_CONFIG.penalties.interactions.total.lowPenalty = 0;
      MATCHMAKING_CONFIG.penalties.interactions.total.highPenalty = 0;
      MATCHMAKING_CONFIG.penalties.interactions.consecutive.sameTeam2 = 0;
      MATCHMAKING_CONFIG.penalties.interactions.consecutive.sameTeam3Plus = 0;
      MATCHMAKING_CONFIG.penalties.interactions.consecutive.total2 = 0;
      MATCHMAKING_CONFIG.penalties.interactions.consecutive.total3Plus = 0;
    }
    
    // Store flags on config for matchmaking functions to access
    MATCHMAKING_CONFIG.simulationOverrides = {
      enableCooldown,
      interactionPenaltyWeight,
    };
  }
  
  const {
    selectedPlayers,
    numberOfCourts,
    partnerships = [],
    targetGames = {},
    gameTypeRestriction = 'all_types',
    playerCategorization = false,
    playersById,
    matchFormat = 'doubles',
  } = config;

  const playersPerSide = getPlayersPerSide(matchFormat);
  const playersPerMatch = getPlayersPerMatch(matchFormat);

  // Initialize simulation state
  const sessionState = {
    gamesPlayedThisSession: {},
    playerInteractions: {},
    matchHistory: [],
    playerWaitMetrics: {},
    totalGamesCompleted: 0,
  };

  const gameHistory = [];
  let waitingList = [...selectedPlayers];
  const playersByIdCopy = JSON.parse(JSON.stringify(playersById)); // Deep copy to avoid mutating original

  // Initialize gamesPlayedThisSession in playersByIdCopy so matchmaking can see it
  selectedPlayers.forEach(playerId => {
    if (playersByIdCopy[playerId]) {
      playersByIdCopy[playerId].gamesPlayedThisSession = 0;
      playersByIdCopy[playerId].effectiveGamesPlayed = 0;
    }
  });

  // Track active games on courts: { courtNumber, game, finishTime }
  // Each court tracks its finishTime (current time + duration)
  const activeCourts = Array(numberOfCourts).fill(null).map((_, i) => ({
    courtNumber: i + 1,
    game: null, // Will store active game
    finishTime: Infinity, // When this court's game will finish
  }));

  // Track upcoming match (only one at a time)
  let upcomingMatch = null;

  // Current simulation time (in arbitrary time units)
  let currentTime = 0;

  // Capture starting ELOs before any games are played (for display in categories table)
  const startingElos = {};
  selectedPlayers.forEach(playerId => {
    const player = playersByIdCopy[playerId];
    if (player) {
      startingElos[playerId] = {
        mixed_elo_rating: player.mixed_elo_rating || 1200,
        levels_elo_rating: player.levels_elo_rating || 1200,
      };
    }
  });

  // Calculate player categories for display (always calculate, even if not used for matchmaking)
  // Use starting ELO ratings from playersById (before any games are played)
  const allPlayers = selectedPlayers.map(id => playersByIdCopy[id]).filter(Boolean);
  const playerCategories = categorizePlayersForSession(allPlayers);
  const categoriesForMatchmaking =
    !isSinglesFormat(matchFormat) && playerCategorization ? playerCategories : null;

  // Default target games if not specified
  const defaultTargetGames = Math.max(...Object.values(targetGames), 3);
  const effectiveTargetGames = {};
  selectedPlayers.forEach(playerId => {
    effectiveTargetGames[playerId] = targetGames[playerId] || defaultTargetGames;
  });

  let iteration = 0;
  const maxIterations = 10000; // Safety limit (increased for more granular simulation)

  // Helper function to get next game to finish (court with lowest finishTime)
  function getNextGameToFinish() {
    let nextCourtIndex = null;
    let earliestFinishTime = Infinity;

    activeCourts.forEach((court, index) => {
      if (court.game !== null && court.finishTime < earliestFinishTime) {
        earliestFinishTime = court.finishTime;
        nextCourtIndex = index;
      }
    });

    if (nextCourtIndex === null) return null;

    return {
      courtIndex: nextCourtIndex,
      courtNumber: activeCourts[nextCourtIndex].courtNumber,
      game: activeCourts[nextCourtIndex].game,
    };
  }

  // Helper function to fill an empty court
  function fillCourt(courtIndex) {
    if (activeCourts[courtIndex].game !== null) {
      return false; // Court already has a game
    }

    let gameData = null;

    // If we have an upcoming match, promote it
    if (upcomingMatch) {
      gameData = {
        ...upcomingMatch,
      };
      upcomingMatch = null;
    } else {
      // Otherwise, use matchmaking to create a new game
      if (waitingList.length < playersPerMatch) {
        return false; // Not enough players
      }

      // DEBUG: Log games played before matchmaking
      if (iteration % 10 === 0 || iteration < 5) {
        const gamesPlayedDebug = waitingList.slice(0, 5).map(id => {
          const player = playersByIdCopy[id];
          return {
            id: id.slice(0, 8),
            games: player?.gamesPlayedThisSession || 0,
            effective: player?.effectiveGamesPlayed || 0,
          };
        });
        console.log(`[SIM DEBUG] Before matchmaking (iteration ${iteration}):`, gamesPlayedDebug);
      }

      const matchmakingResult = getBestCourtAssignmentsTwoStage(
        waitingList,
        1, // Only need 1 court
        sessionState,
        playersByIdCopy,
        false, // contextAware
        partnerships,
        sessionState.playerWaitMetrics,
        [], // scheduledGames
        gameTypeRestriction,
        categoriesForMatchmaking,
        matchFormat
      );

      const courts = matchmakingResult.courts || [];
      if (courts.length === 0) {
        return false; // No valid game can be created
      }

      const court = courts[0];
      if (court.team1.length !== playersPerSide || court.team2.length !== playersPerSide) {
        return false;
      }

      gameData = {
        team1PlayerIds: court.team1,
        team2PlayerIds: court.team2,
        matchmakingRelaxed: court.matchmakingRelaxed || false,
        matchmakingRelaxationDetails: court.matchmakingRelaxationDetails || [],
      };

      // Remove players from waiting list
      const playersInGame = [...court.team1, ...court.team2];
      waitingList = waitingList.filter(id => !playersInGame.includes(id));
    }

    // Assign random duration 7-11 and calculate finishTime
    const duration = 7 + Math.floor(Math.random() * 5); // Random duration 7-11
    const finishTime = currentTime + duration;

    activeCourts[courtIndex].game = {
      courtNumber: activeCourts[courtIndex].courtNumber,
      ...gameData,
    };
    activeCourts[courtIndex].finishTime = finishTime;

    // CRITICAL FIX: Reset courtsObserved for players who just started playing
    // This ensures they fail cooldown filter until they finish and wait again
    const playersInGame = [...gameData.team1PlayerIds, ...gameData.team2PlayerIds];
    playersInGame.forEach(playerId => {
      if (sessionState.playerWaitMetrics[playerId]) {
        sessionState.playerWaitMetrics[playerId].courtsObserved = 0;
      }
    });

    return true;
  }

  // Helper function to complete a game
  function completeGame(courtIndex) {
    const activeGame = activeCourts[courtIndex].game;
    if (!activeGame) return;

    const { team1PlayerIds, team2PlayerIds } = activeGame;

    const team1Players = team1PlayerIds.map(id => playersByIdCopy[id]).filter(Boolean);
    const team2Players = team2PlayerIds.map(id => playersByIdCopy[id]).filter(Boolean);

    if (team1Players.length !== playersPerSide || team2Players.length !== playersPerSide) {
      activeCourts[courtIndex].game = null;
      return;
    }

    // Store starting ELOs before calculating changes
    const team1PlayersWithStartingElo = team1Players.map(p => ({
      ...p,
      starting_mixed_elo: p.mixed_elo_rating || 1200,
      starting_levels_elo: p.levels_elo_rating || 1200,
    }));
    const team2PlayersWithStartingElo = team2Players.map(p => ({
      ...p,
      starting_mixed_elo: p.mixed_elo_rating || 1200,
      starting_levels_elo: p.levels_elo_rating || 1200,
    }));

    // Detect game type
    const gameType = detectGameType(team1Players, team2Players);
    const gameTypeId = gameType.id;
    const isMixed = gameTypeId === 1;
    const isSinglesMatch = team1Players.length === 1 && team2Players.length === 1;

    const result = simulateGameResult(
      team1Players,
      team2Players,
      playersByIdCopy,
      isSinglesMatch ? false : isMixed,
      isSinglesMatch
    );

    let updatedPlayers;
    if (isSinglesMatch && (gameTypeId === 4 || gameTypeId === 5)) {
      updatedPlayers = calculateSinglesRatedEloChanges(team1Players, team2Players, result, gameTypeId);
    } else if (isSinglesMatch && gameTypeId === 7) {
      updatedPlayers = [...team1Players, ...team2Players];
    } else {
      updatedPlayers = calculateEloChanges(team1Players, team2Players, result, isMixed);
    }

    updatedPlayers.forEach((updatedPlayer) => {
      if (!playersByIdCopy[updatedPlayer.id]) return;
      const prev = playersByIdCopy[updatedPlayer.id];
      if (isSinglesMatch && (gameTypeId === 4 || gameTypeId === 5)) {
        playersByIdCopy[updatedPlayer.id] = {
          ...prev,
          singles_elo_rating: updatedPlayer.singles_elo_rating,
          singles_games_played: updatedPlayer.singles_games_played ?? prev.singles_games_played,
        };
      } else if (isSinglesMatch && gameTypeId === 7) {
        playersByIdCopy[updatedPlayer.id] = { ...prev };
      } else {
        playersByIdCopy[updatedPlayer.id] = {
          ...prev,
          mixed_elo_rating: updatedPlayer.mixed_elo_rating,
          levels_elo_rating: updatedPlayer.levels_elo_rating,
          mixed_games_played: updatedPlayer.mixed_games_played || 0,
          levels_games_played: updatedPlayer.levels_games_played || 0,
        };
      }
    });

    // Update player objects with ending ELOs for game record
    const team1PlayersWithEndingElo = team1PlayersWithStartingElo.map((p) => {
      const updated = updatedPlayers.find((u) => u.id === p.id);
      if (!updated) return p;
      if (isSinglesMatch && (gameTypeId === 4 || gameTypeId === 5)) {
        return { ...p, singles_elo_rating: updated.singles_elo_rating };
      }
      return {
        ...p,
        mixed_elo_rating: updated.mixed_elo_rating,
        levels_elo_rating: updated.levels_elo_rating,
      };
    });
    const team2PlayersWithEndingElo = team2PlayersWithStartingElo.map((p) => {
      const updated = updatedPlayers.find((u) => u.id === p.id);
      if (!updated) return p;
      if (isSinglesMatch && (gameTypeId === 4 || gameTypeId === 5)) {
        return { ...p, singles_elo_rating: updated.singles_elo_rating };
      }
      return {
        ...p,
        mixed_elo_rating: updated.mixed_elo_rating,
        levels_elo_rating: updated.levels_elo_rating,
      };
    });

    // Create game record
    const finishTime = activeCourts[courtIndex].finishTime;
    const game = {
      id: `sim_${gameHistory.length + 1}`,
      createdAt: new Date().toISOString(),
      startTime: new Date(Date.now() + (finishTime - (finishTime - currentTime)) * 60000).toISOString(), // Approximate start time
      endTime: new Date(Date.now() + finishTime * 60000).toISOString(),
      team1PlayerIds,
      team2PlayerIds,
      team1Players: team1PlayersWithEndingElo,
      team2Players: team2PlayersWithEndingElo,
      result,
      gameType: gameTypeId,
      isMixed,
      team1Score: result.team1Score,
      team2Score: result.team2Score,
      winningTeam: result.winner,
      matchmakingRelaxed: activeGame.matchmakingRelaxed || false,
      matchmakingRelaxationDetails: activeGame.matchmakingRelaxationDetails || [],
    };

    gameHistory.push(game);
    updateSessionState(game, sessionState);
    
    // CRITICAL: Update gamesPlayedThisSession in playersByIdCopy so matchmaking can see it
    const playersOnCourt = [...team1PlayerIds, ...team2PlayerIds];
    playersOnCourt.forEach(playerId => {
      if (playersByIdCopy[playerId]) {
        const newGamesCount = sessionState.gamesPlayedThisSession[playerId] || 0;
        playersByIdCopy[playerId].gamesPlayedThisSession = newGamesCount;
        playersByIdCopy[playerId].effectiveGamesPlayed = newGamesCount;
        
        // DEBUG: Log if a player is getting way ahead
        const target = effectiveTargetGames[playerId];
        if (newGamesCount > target + 2) {
          console.warn(`[SIM WARNING] Player ${playerId.slice(0, 8)} has ${newGamesCount} games (target: ${target})`);
        }
      }
    });

    // Reset courtsObserved for players who just finished
    playersOnCourt.forEach(playerId => {
      if (sessionState.playerWaitMetrics[playerId]) {
        sessionState.playerWaitMetrics[playerId].courtsObserved = 0;
      }
    });

    // Move players back to waiting list
    waitingList = [...waitingList, ...playersOnCourt];

    // Clear the court
    activeCourts[courtIndex].game = null;
    activeCourts[courtIndex].finishTime = Infinity;
  }

  // Helper function to create upcoming match
  function createUpcomingMatch() {
    if (upcomingMatch !== null) {
      return; // Already have an upcoming match
    }

    if (waitingList.length < playersPerMatch) {
      return; // Not enough players
    }

    // DEBUG: Log games played before creating upcoming match
    const gamesPlayedDebug = waitingList.slice(0, 5).map(id => {
      const player = playersByIdCopy[id];
      return {
        id: id.slice(0, 8),
        games: player?.gamesPlayedThisSession || 0,
        effective: player?.effectiveGamesPlayed || 0,
      };
    });
    console.log(`[SIM DEBUG] Creating upcoming match, waiting list games:`, gamesPlayedDebug);

    const matchmakingResult = getBestCourtAssignmentsTwoStage(
      waitingList,
      1, // Only need 1 match
      sessionState,
      playersByIdCopy,
      false, // contextAware
      partnerships,
      sessionState.playerWaitMetrics,
      [], // scheduledGames
      gameTypeRestriction,
      categoriesForMatchmaking,
      matchFormat
    );

    const courts = matchmakingResult.courts || [];
    if (courts.length === 0) {
      console.warn(`[SIM WARNING] Could not create upcoming match - no valid games`);
      return; // No valid game can be created
    }

    const court = courts[0];
    if (court.team1.length !== playersPerSide || court.team2.length !== playersPerSide) {
      console.warn(`[SIM WARNING] Upcoming match has invalid teams`);
      return;
    }

    // DEBUG: Log which players were selected for upcoming match
    const selectedPlayersDebug = [...court.team1, ...court.team2].map(id => {
      const player = playersByIdCopy[id];
      return {
        id: id.slice(0, 8),
        games: player?.gamesPlayedThisSession || 0,
      };
    });
    console.log(`[SIM DEBUG] Upcoming match players:`, selectedPlayersDebug);

    // Remove players from waiting list (they're in upcoming match)
    const playersInMatch = [...court.team1, ...court.team2];
    waitingList = waitingList.filter(id => !playersInMatch.includes(id));

    upcomingMatch = {
      team1PlayerIds: court.team1,
      team2PlayerIds: court.team2,
    };
  }

  // Initial setup: Fill all courts one by one
  for (let i = 0; i < numberOfCourts; i++) {
    fillCourt(i);
  }

  // Create initial upcoming match if all courts are filled
  const allCourtsFilled = activeCourts.every(court => court.game !== null);
  if (allCourtsFilled && waitingList.length >= playersPerMatch) {
    createUpcomingMatch();
  }

  // Main simulation loop
  while (iteration < maxIterations) {
    iteration++;

    // Check if minimum games played has reached the target
    // Since matchmaking prioritizes players at minimum games, everyone should finish around the same number
    const gamesPlayedArray = selectedPlayers.map(playerId => {
      return sessionState.gamesPlayedThisSession[playerId] || 0;
    });
    
    if (gamesPlayedArray.length === 0) {
      break; // No players
    }

    const minGamesPlayed = Math.min(...gamesPlayedArray);
    const maxGamesPlayed = Math.max(...gamesPlayedArray);
    
    // Get the target games (use minimum of all targets for fairness)
    const targetGamesArray = selectedPlayers.map(playerId => effectiveTargetGames[playerId]);
    const targetGamesMin = Math.min(...targetGamesArray);
    
    // DEBUG: Log games distribution every 20 iterations
    if (iteration % 20 === 0 || iteration < 5) {
      const gamesDistribution = selectedPlayers.map(playerId => {
        const games = sessionState.gamesPlayedThisSession[playerId] || 0;
        const player = playersByIdCopy[playerId];
        const gamesInPlayerObj = player?.gamesPlayedThisSession || 0;
        return {
          id: playerId.slice(0, 8),
          sessionState: games,
          playerObj: gamesInPlayerObj,
          match: games === gamesInPlayerObj ? '✓' : '✗ MISMATCH',
        };
      }).sort((a, b) => b.sessionState - a.sessionState);
      
      console.log(`[SIM DEBUG] Games distribution (iteration ${iteration}):`, {
        min: minGamesPlayed,
        max: maxGamesPlayed,
        target: targetGamesMin,
        players: gamesDistribution.slice(0, 5),
      });
      
      // Check for mismatches
      const mismatches = gamesDistribution.filter(p => p.match === '✗ MISMATCH');
      if (mismatches.length > 0) {
        console.error(`[SIM ERROR] Found ${mismatches.length} players with mismatched games counts!`, mismatches);
      }
    }
    
    // Safety check: Stop if any player exceeds target by more than 5 games
    // This prevents runaway simulations where matchmaking isn't working correctly
    const anyPlayerOverLimit = selectedPlayers.some(playerId => {
      const gamesPlayed = sessionState.gamesPlayedThisSession[playerId] || 0;
      const target = effectiveTargetGames[playerId];
      return gamesPlayed > target + 5;
    });
    
    if (anyPlayerOverLimit) {
      const offenders = selectedPlayers
        .map(playerId => ({
          id: playerId.slice(0, 8),
          games: sessionState.gamesPlayedThisSession[playerId] || 0,
          target: effectiveTargetGames[playerId],
          inPlayerObj: playersByIdCopy[playerId]?.gamesPlayedThisSession || 0,
        }))
        .filter(p => p.games > p.target + 5);
      
      console.error(`[SIM ERROR] Simulation stopped: Players exceeded target by more than 5:`, offenders);
      // Complete any remaining active games
      activeCourts.forEach((court, index) => {
        if (court.game !== null) {
          completeGame(index);
        }
      });
      break;
    }
    
    // End simulation when minimum games reaches target
    // Since matchmaking always selects players at minimum first, everyone should be within 1-2 games of each other
    if (minGamesPlayed >= targetGamesMin) {
      // Complete any remaining active games
      activeCourts.forEach((court, index) => {
        if (court.game !== null) {
          completeGame(index);
        }
      });
      break;
    }
    
    // Safety check: if max games is way ahead of min (more than 3 games), something is wrong
    // This shouldn't happen with proper matchmaking, but it's a safety net
    if (maxGamesPlayed - minGamesPlayed > 3) {
      console.warn(`Simulation: Large gap detected - min: ${minGamesPlayed}, max: ${maxGamesPlayed}. This shouldn't happen with fair matchmaking.`);
    }

    // Find next game to finish
    const nextGame = getNextGameToFinish();
    if (!nextGame) {
      // No active games, try to fill empty courts
      let filledAny = false;
      for (let i = 0; i < numberOfCourts; i++) {
        if (fillCourt(i)) {
          filledAny = true;
        }
      }
      if (!filledAny) {
        // Can't fill any courts, simulation stuck
        break;
      }
      continue;
    }

    // Advance time to when the next game finishes
    currentTime = activeCourts[nextGame.courtIndex].finishTime;

    // Complete the game (players return to waiting list)
    completeGame(nextGame.courtIndex);

    // Update wait metrics for players in waiting list (they just observed a game finish)
    // This happens AFTER game completes, matching real session behavior
    updateWaitMetrics(waitingList, sessionState.playerWaitMetrics);

    // Try to fill the now-empty court
    fillCourt(nextGame.courtIndex);

    // Create new upcoming match if all courts are filled and we don't have one
    const allCourtsFilled = activeCourts.every(court => court.game !== null);
    if (allCourtsFilled && upcomingMatch === null && waitingList.length >= playersPerMatch) {
      createUpcomingMatch();
    }
  }

  // Restore original config and feature flags
  Object.assign(MATCHMAKING_CONFIG, originalConfig);
  MATCHMAKING_CONFIG.featureFlags = originalFeatureFlags;

  return {
    gameHistory,
    sessionState,
    playersById: playersByIdCopy,
    iterations: iteration,
    playerCategories: playerCategories, // Full categorization result with all metadata
    startingElos: startingElos, // Starting ELOs used for categorization
  };
}

